import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function TermsOfService() {
  const [lastUpdated] = useState("January 21, 2025");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="bg-white/90 backdrop-blur shadow-xl border-purple-200">
          <div className="p-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-600 mb-8">Last updated: {lastUpdated}</p>

            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-8 text-gray-800">
                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">1. Acceptance of Terms</h2>
                  <p className="leading-relaxed">
                    By accessing and using SnuggleCrafters.com ("the Service"), you agree to be bound by these Terms of Service ("Terms"). 
                    If you do not agree to these Terms, please do not use the Service. SnuggleCrafters.com is operated by VibeCaaS.com, 
                    a division of NeuralQuantum.ai.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">2. Description of Service</h2>
                  <p className="leading-relaxed mb-4">
                    SnuggleCrafters.com is an AI-powered bedtime story generator that creates personalized stories for children. 
                    The Service includes:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>AI-generated personalized bedtime stories</li>
                    <li>Audio narration with synchronized word highlighting</li>
                    <li>Story-based merchandise generation</li>
                    <li>Physical book printing services</li>
                    <li>Digital marketplace for story-related products</li>
                  </ul>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">3. User Accounts</h2>
                  <div className="space-y-4">
                    <p className="leading-relaxed">
                      To access certain features of the Service, you must create an account. You agree to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide accurate, current, and complete information</li>
                      <li>Maintain the security of your password and account</li>
                      <li>Notify us immediately of any unauthorized access</li>
                      <li>Be responsible for all activities under your account</li>
                    </ul>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">4. Subscription and Payment</h2>
                  <div className="space-y-4">
                    <p className="leading-relaxed">
                      <strong>Free Trial:</strong> New users receive a 7-day free trial with full access to all features.
                    </p>
                    <p className="leading-relaxed">
                      <strong>Subscription:</strong> After the trial period, continued access requires a paid subscription 
                      at $9.99/month USD. Subscriptions automatically renew unless canceled.
                    </p>
                    <p className="leading-relaxed">
                      <strong>Cancellation:</strong> You may cancel your subscription at any time through the customer portal. 
                      Access continues until the end of your billing period.
                    </p>
                    <p className="leading-relaxed">
                      <strong>Refunds:</strong> No refunds are provided for partial billing periods. All sales of physical 
                      products and marketplace items are final.
                    </p>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">5. Content and Intellectual Property</h2>
                  <div className="space-y-4">
                    <p className="leading-relaxed">
                      <strong>Generated Content:</strong> Stories generated for you are yours to keep and use for personal, 
                      non-commercial purposes. You may purchase physical copies of your stories.
                    </p>
                    <p className="leading-relaxed">
                      <strong>Service Content:</strong> All other content, including the SnuggleCrafters.com platform, designs, 
                      and trademarks, remain the property of VibeCaaS.com/NeuralQuantum.ai.
                    </p>
                    <p className="leading-relaxed">
                      <strong>User Input:</strong> By submitting information for story generation, you grant us a license 
                      to process that information to provide the Service.
                    </p>
                  </div>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">6. Privacy and Data Protection</h2>
                  <p className="leading-relaxed">
                    Your use of the Service is also governed by our Privacy Policy. We are committed to protecting your 
                    privacy and handling your data in accordance with applicable laws including GDPR and CCPA.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">7. Acceptable Use</h2>
                  <p className="leading-relaxed mb-4">You agree not to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Use the Service for any illegal or harmful purpose</li>
                    <li>Attempt to access unauthorized areas of the Service</li>
                    <li>Interfere with or disrupt the Service</li>
                    <li>Use automated systems to access the Service</li>
                    <li>Resell or commercially distribute generated content without permission</li>
                  </ul>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">8. Children's Use</h2>
                  <p className="leading-relaxed">
                    SnuggleCrafters.com is designed for use by parents and guardians to create stories for children. 
                    Children under 13 should not create accounts or use the Service without parental supervision.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">9. Disclaimer of Warranties</h2>
                  <p className="leading-relaxed">
                    The Service is provided "as is" without warranties of any kind. We do not guarantee that the Service 
                    will be uninterrupted, error-free, or meet your specific requirements. AI-generated content may contain 
                    inaccuracies or unexpected elements.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">10. Limitation of Liability</h2>
                  <p className="leading-relaxed">
                    To the maximum extent permitted by law, VibeCaaS.com/NeuralQuantum.ai shall not be liable for any indirect, 
                    incidental, special, consequential, or punitive damages arising from your use of the Service.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">11. Changes to Terms</h2>
                  <p className="leading-relaxed">
                    We reserve the right to modify these Terms at any time. We will notify users of significant changes 
                    via email or through the Service. Continued use after changes constitutes acceptance of the new Terms.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">12. Governing Law</h2>
                  <p className="leading-relaxed">
                    These Terms are governed by the laws of the United States and the State of California, 
                    without regard to conflict of law principles.
                  </p>
                </section>

                <Separator className="bg-purple-100" />

                <section>
                  <h2 className="text-2xl font-semibold text-purple-700 mb-4">13. Contact Information</h2>
                  <p className="leading-relaxed">
                    For questions about these Terms of Service, please contact us at:
                  </p>
                  <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                    <p className="font-semibold">VibeCaaS.com Legal Department</p>
                    <p>Email: legal@VibeCaaS.com</p>
                    <p>Website: https://VibeCaaS.com</p>
                  </div>
                </section>
              </div>
            </ScrollArea>
          </div>
        </Card>
      </div>
    </div>
  );
}